import { useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadOutlined } from '@ant-design/icons';
import { message, Input, Modal, Typography, Upload, Space } from 'antd';
import type { RcFile, UploadFile } from 'antd/lib/upload';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import type { Polygon } from 'geojson';
import { useCustomAreas } from '../../../../hooks/SimViewer/useCustomAreas';
import { scrollbarStyle } from '../../../../styles/scrollbar';
import {
  parseGeoJson,
  parseWkt,
  calculateCentroid,
} from '../../../../utils/geospatial';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

const CustomAreaUploadModal = ({ isOpen, onClose }: Props): ReactNode => {
  const [file, setFile] = useState<UploadFile<string> | null>(null);
  const [polygons, setPolygons] = useState<Polygon[]>([]);
  const [name, setName] = useState<string>('');

  const { t } = useTranslation();

  const { addCustomArea } = useCustomAreas();

  return (
    <Modal
      title={t('importCustomArea')}
      centered
      okText={t('load')}
      cancelText={t('cancel')}
      mask={false}
      width={600}
      okButtonProps={{
        size: 'small',
        disabled: polygons.length === 0 || !name,
      }}
      cancelButtonProps={{ size: 'small' }}
      open={isOpen}
      onCancel={(): void => {
        setFile(null);
        setPolygons([]);
        setName('');
        onClose();
      }}
      onOk={(): void => {
        if (polygons.length === 0) {
          return;
        }
        polygons.forEach((polygon: Polygon): void => {
          addCustomArea({
            key: uuidv4(),
            type: 'polygon',
            label: name,
            radius: 0,
            minutes: 0,
            center: calculateCentroid(polygon),
            hide: false,
            geometry: {
              coordinates: polygon.coordinates[0],
            },
          });
        });
        setFile(null);
        setPolygons([]);
        setName('');
        onClose();
      }}
    >
      <UploadContainer
        accept=".geojson,.json,.wkt,.txt,.text,application/json,application/geo+json,application/octet-stream,text/plain"
        multiple={false}
        directory={false}
        listType="text"
        fileList={file ? [file] : []}
        beforeUpload={(file: RcFile): boolean => {
          const reader = new FileReader();
          reader.onload = (event: ProgressEvent<FileReader>) => {
            const text =
              event.target?.result instanceof ArrayBuffer
                ? new TextDecoder('utf-8').decode(event.target.result)
                : event.target?.result || '';

            const polygons = [...parseGeoJson(text), ...parseWkt(text)];
            if (polygons.length > 0) {
              setFile({
                uid: file.uid,
                name: file.name,
                response: text,
                status: 'done',
              });
              setPolygons(polygons);
              setName(file.name.split('.')[0]);
            } else {
              setFile({
                uid: file.uid,
                name: file.name,
                response: text,
                status: 'error',
              });
              setPolygons([]);
              setName('');
              message.error(t('message.failedToUploadFile'));
            }
          };
          if (file.size <= MAX_FILE_SIZE) {
            reader.readAsText(file);
          } else {
            setFile({
              uid: file.uid,
              name: file.name,
              response: '',
              status: 'error',
            });
            setPolygons([]);
            setName('');
            message.error(
              t('message.fileTooLarge', { max: MAX_FILE_SIZE / (1024 * 1024) })
            );
          }

          // AntdのUploadコンポーネントは自動でファイルをさらに別のサーバーにアップロードしようとするため、falseを返してストップする
          return false;
        }}
        onRemove={(): void => {
          setFile(null);
          setPolygons([]);
          setName('');
        }}
      >
        {file?.response ? (
          <ScrollableParagraph>{file.response}</ScrollableParagraph>
        ) : (
          <VerticalContainer>
            <UploadOutlined
              style={{ fontSize: 32, color: '#707070', marginBlock: 16 }}
            />
            <Typography.Text strong style={{ color: '#707070' }}>
              {t('clickOrDropToUpload')}
            </Typography.Text>
            <Typography.Text style={{ color: '#707070', opacity: 0.9 }}>
              {t('message.supportedFileFormats')}
            </Typography.Text>
          </VerticalContainer>
        )}
      </UploadContainer>
      {polygons.length > 0 && (
        <Space direction="horizontal" style={{ width: '100%', marginTop: 16 }}>
          <Space.Compact block>{t('customAreaName')}</Space.Compact>
          <Input
            value={name}
            style={{ width: 320 }}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              setName(event.target.value);
            }}
          />
        </Space>
      )}
    </Modal>
  );
};

const UploadContainer = styled(Upload.Dragger)`
  &.ant-upload.ant-upload-drag .ant-upload {
    padding: 16px;
  }
`;

const ScrollableParagraph = styled(Typography.Paragraph)`
  white-space: pre-wrap;
  word-break: break-all;
  font-weight: 600;
  text-align: left;
  margin: 0;
  overflow: auto;
  max-height: 240px;
  ${scrollbarStyle}

  &.ant-typography {
    margin-bottom: 0;
  }
`;

const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export { CustomAreaUploadModal };
